import { useEffect, useState } from "react";
import { getAccessToken } from "../api/client";

/** Fundo (imagem) da instituição para uma aplicação, pronto para o AppShell. */
export interface InstitutionBackground {
  backgroundImageUrl: string;
  backgroundFit: "cover" | "contain" | "full" | "auto";
  backgroundPosition: string;
  backgroundRepeat: boolean;
  backgroundOpacity: number;
}

const FITS = ["cover", "contain", "full", "auto"] as const;
function normFit(f?: string): InstitutionBackground["backgroundFit"] {
  return (FITS as readonly string[]).includes(f ?? "") ? (f as InstitutionBackground["backgroundFit"]) : "cover";
}

/**
 * Busca o branding (imagem de fundo) da instituição selecionada para o módulo
 * informado. Faz 2 chamadas autenticadas via gateway (mesma origem em dev):
 *   1. GET /api/branding/me?module=<key>  (Central) -> { branding }
 *   2. GET /api/files/v1/files/<hash>/url (file-service) -> URL presigned
 * A URL presigned é usada direto no background-image (sem auth/CORS no CSS).
 * Refaz periodicamente pois a URL presigned expira (~10min).
 */
export function useInstitutionBackground(
  moduleKey: string,
  tenantId?: string | null,
): InstitutionBackground | undefined {
  const [bg, setBg] = useState<InstitutionBackground | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    const auth = (): Record<string, string> => {
      const t = getAccessToken();
      return t ? { Authorization: `Bearer ${t}` } : {};
    };

    async function load() {
      if (!getAccessToken()) { if (!cancelled) setBg(undefined); return; }
      try {
        const bRes = await fetch(`/api/branding/me?module=${encodeURIComponent(moduleKey)}`, {
          headers: auth(), credentials: "include",
        });
        if (!bRes.ok) { if (!cancelled) setBg(undefined); return; }
        const { branding } = await bRes.json();
        if (!branding) { if (!cancelled) setBg(undefined); return; }
        const uRes = await fetch(`/api/files/v1/files/${branding.fileHash}/url`, {
          headers: auth(), credentials: "include",
        });
        if (!uRes.ok) { if (!cancelled) setBg(undefined); return; }
        const { url } = await uRes.json();
        if (!cancelled && url) {
          setBg({
            backgroundImageUrl: url,
            backgroundFit: normFit(branding.fit),
            backgroundPosition: branding.position || "center",
            backgroundRepeat: !!branding.repeat,
            backgroundOpacity: Math.min(1, Math.max(0, (branding.opacity ?? 100) / 100)),
          });
        }
      } catch {
        if (!cancelled) setBg(undefined);
      }
    }

    load();
    const timer = setInterval(load, 8 * 60 * 1000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [moduleKey, tenantId]);

  return bg;
}
